import { useState, useEffect } from "react";
import { useTheme } from "next-themes";
import { useAuth } from "@/contexts/AuthContext";
import { F, th as palette, Btn } from "@/lib/bricks";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Page, PageHeader, StatusTabs, List, Row, Empty, TextLink, Loading, FieldLabel, humanize } from "@/components/calm/Calm";

const AUDIENCE_TYPES = ['Buyer', 'Seller', 'Landlord', 'Tenant', 'Referral', 'Lead'];

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
}

const EMPTY_CAMPAIGN = { name: '', subject: '', body: '', audience_value: 'Lead' };

export default function BoostPage() {
  const { resolvedTheme } = useTheme();
  const { session } = useAuth();
  const dark = resolvedTheme === 'dark';
  const p = palette(dark);

  const [activeTab, setActiveTab] = useState('All');
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(EMPTY_CAMPAIGN);
  const [creating, setCreating] = useState(false);
  const [sendTarget, setSendTarget] = useState(null);
  const [sendCount, setSendCount] = useState(null);
  const [sending, setSending] = useState(false);
  const [emailConnected, setEmailConnected] = useState(false);

  const BASE = process.env.REACT_APP_BACKEND_URL;
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` };

  const load = () => {
    Promise.all([
      fetch(`${BASE}/api/boost/campaigns`, { headers }).then(r => r.json()).catch(() => []),
      fetch(`${BASE}/api/boost/status`, { headers }).then(r => r.json()).catch(() => ({})),
    ]).then(([d, st]) => {
      setCampaigns(Array.isArray(d) ? d : []);
      setEmailConnected(!!(st && (st.email_connected || st.ready)));
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleCreate = async () => {
    if (!form.name || !form.subject || !form.body) return toast.error("Name, subject, and body are required");
    setCreating(true);
    try {
      const res = await fetch(`${BASE}/api/boost/campaigns`, { method: 'POST', headers, body: JSON.stringify(form) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Failed to create campaign');
      toast.success("Campaign created as draft");
      setShowNew(false);
      setForm(EMPTY_CAMPAIGN);
      load();
    } catch (e) { toast.error(e.message || "Failed to create campaign"); }
    setCreating(false);
  };

  const openSendConfirm = async (campaign) => {
    if (!emailConnected) {
      toast.error("Email sending is not connected yet. The draft is saved.");
      return;
    }
    setSendTarget(campaign);
    setSendCount(null);
    try {
      const res = await fetch(`${BASE}/api/boost/campaigns/${campaign.id}/audience-count`, { headers });
      const d = await res.json();
      setSendCount(d.count);
    } catch { setSendCount(campaign.recipient_count); }
  };

  const handleSend = async () => {
    if (!sendTarget) return;
    setSending(true);
    try {
      const res = await fetch(`${BASE}/api/boost/campaigns/${sendTarget.id}/send`, { method: 'POST', headers });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Send failed');
      toast.success(`Sent to ${d.sent_count} of ${d.total} recipients`);
      setSendTarget(null);
      load();
    } catch (e) { toast.error(e.message || "Failed to send campaign"); }
    setSending(false);
  };

  const TABS = ['All', 'Draft', 'Sent'];
  const filtered = campaigns.filter(c => activeTab === 'All' || c.status === activeTab.toLowerCase());
  const totalSent = campaigns.reduce((sum, c) => sum + (c.sent_count || 0), 0);

  if (loading) return <Loading />;

  const link = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 13, color: p.ink, fontFamily: F.display, textDecoration: 'underline', textUnderlineOffset: 3, textDecorationColor: p.hairS };

  return (
    <Page testid="boost-page" width={960}>
      <PageHeader title="Boost"
        subtitle={emailConnected
          ? `${campaigns.length} campaign${campaigns.length === 1 ? '' : 's'}, ${totalSent} emails sent`
          : 'Drafts save to this office. Sending turns on once email is connected.'}
        action={<Btn variant="primary" dark={dark} onClick={() => setShowNew(true)}>New campaign</Btn>} />

      <StatusTabs label="Status" options={TABS.map(t => ({ key: t, label: t }))} value={activeTab} onChange={setActiveTab} testid="boost-tabs" />

      {filtered.length === 0 ? (
        <Empty action={activeTab === 'All' ? <TextLink onClick={() => setShowNew(true)}>Write your first campaign</TextLink> : null}>
          {activeTab === 'All' ? 'No campaigns yet. Email a segment of your contacts in a few lines.' : `No ${activeTab.toLowerCase()} campaigns.`}
        </Empty>
      ) : (
        <List testid="boost-campaigns">
          {filtered.map(c => (
            <Row key={c.id} testid={`campaign-${c.id}`}
              onClick={c.status === 'draft' ? () => openSendConfirm(c) : undefined}
              title={c.name}
              meta={[c.subject, `${c.audience_value} contacts`, { text: `${c.status === 'sent' ? c.sent_count : c.recipient_count || 0} recipients`, figure: false }]}
              next={c.status === 'sent' ? `Sent ${fmtDate(c.sent_at)}` : (emailConnected ? 'Ready to send' : 'Saved as a draft')}
              status={humanize(c.status || 'draft')}
              aside={c.status === 'draft' && emailConnected ? <button style={link} onClick={e => { e.stopPropagation(); openSendConfirm(c); }}>Send</button> : null} />
          ))}
        </List>
      )}

      {/* New Campaign Dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent style={{ maxWidth: 520, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)', maxHeight: '90vh', overflowY: 'auto' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>New campaign</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>Compose a one-time email blast to a segment of your CRM contacts.</DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 20 }}>
            <div><FieldLabel>Campaign name</FieldLabel>
              <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Spring listings blast"
                style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div><FieldLabel>Audience</FieldLabel>
              <select value={form.audience_value} onChange={e => setForm({ ...form, audience_value: e.target.value })}
                style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: p.bg, outline: 'none' }}>
                {AUDIENCE_TYPES.map(v => <option key={v} value={v}>{v} contacts</option>)}
              </select>
            </div>
            <div><FieldLabel>Subject line</FieldLabel>
              <input type="text" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} placeholder="New listings you'll want to see"
                style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div><FieldLabel>Email body (HTML)</FieldLabel>
              <textarea value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} rows={8} placeholder="<p>Hi {{first_name}}, ...</p>"
                style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 13, fontFamily: F.mono, color: p.ink, background: 'transparent', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
            </div>
          </div>
          <DialogFooter style={{ marginTop: 24, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setShowNew(false)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} onClick={handleCreate} disabled={creating}>{creating ? 'Creating...' : 'Save as draft'}</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Confirm Dialog */}
      <Dialog open={!!sendTarget} onOpenChange={(open) => !open && setSendTarget(null)}>
        <DialogContent style={{ maxWidth: 440, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>Send "{sendTarget?.name}"?</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>
              This will immediately email {sendCount === null ? '...' : sendCount} {sendTarget?.audience_value} contact{sendCount === 1 ? '' : 's'}. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter style={{ marginTop: 24, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setSendTarget(null)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} onClick={handleSend} disabled={sending || sendCount === 0}>{sending ? 'Sending...' : 'Send now'}</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
